import { createRouter, createWebHistory, RouteRecordRaw, RouteMeta } from 'vue-router';
import { createAuthGuard } from '@auth0/auth0-vue';
import MainLayout from '@/layouts/MainLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import Home from '@/views/common/Home.vue';
import Login from '@/views/common/user/Login.vue';
import Logout from '@/views/common/user/Logout.vue';
import Profile from '@/views/common/user/Profile.vue';
import NotFound from '@/views/common/NotFound.vue';
import Forbidden from '@/views/common/Forbidden.vue';
import VendorList from '@/views/vendors/VendorList.vue';
import VendorCreate from '@/views/vendors/VendorCreate.vue';
import VendorImport from '@/views/vendors/VendorImport.vue';
import VendorDetail from '@/views/vendors/VendorDetail.vue';
import CapacityImport from '@/views/capacity/CapacityImport.vue';
import CapacityCalendar from '@/views/capacity/CapacityCalendar.vue';
import CapacityAssignment from '@/views/capacity/CapacityAssignment.vue';
import SchedulingBoard from '@/views/capacity/SchedulingBoard.vue';
import CustomerMappingList from '@/views/customers/CustomerMappingList.vue';
import PoActivity from '@/views/activity/PoActivity.vue';
import PoTracker from '@/views/poTracker/PoTracker.vue';
import ManageAccess from '@/views/admin/ManageAccess.vue';
import { useAccessStore } from '@/store/accessStore';
import { useAuthStore } from '@/store/authStore';
import { useAccess } from '@/composables/useAccess';
import { resolveAccessDenial } from '@/router/accessGuard';
import { PERMISSIONS } from '@/types/access';

interface CustomRouteMeta extends RouteMeta {
  title?: string;
  requiresPermission?: string | string[];
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'Home',
        component: Home,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'vendors',
        name: 'VendorList',
        component: VendorList,
        meta: { title: 'Vendors | Manufacturing Partners Portal', requiresPermission: PERMISSIONS.READ_VENDORS } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'vendors/new',
        name: 'VendorCreate',
        component: VendorCreate,
        meta: { title: 'Add Vendor | Manufacturing Partners Portal', requiresPermission: [PERMISSIONS.READ_VENDORS, PERMISSIONS.WRITE_VENDORS] } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'vendors/:id',
        name: 'VendorDetail',
        component: VendorDetail,
        meta: { title: 'Vendor | Manufacturing Partners Portal', requiresPermission: PERMISSIONS.READ_VENDORS } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'vendors/import/:odooId',
        name: 'VendorImport',
        component: VendorImport,
        meta: { title: 'Import Vendor | Manufacturing Partners Portal', requiresPermission: [PERMISSIONS.READ_VENDORS, PERMISSIONS.WRITE_VENDORS] } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'capacity-import',
        name: 'CapacityImport',
        component: CapacityImport,
        meta: {
          title: 'Capacity Import | Manufacturing Partners Portal',
          requiresPermission: PERMISSIONS.IMPORT_CAPACITY,
        } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'capacity-calendar',
        name: 'CapacityCalendar',
        component: CapacityCalendar,
        meta: {
          title: 'Capacity Calendar | Manufacturing Partners Portal',
          requiresPermission: PERMISSIONS.READ_CAPACITY,
        } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'scheduling',
        name: 'Scheduling',
        component: SchedulingBoard,
        meta: {
          title: 'Scheduling | Manufacturing Partners Portal',
          // The board is a PO view drawn on the capacity calendar's grid, so it needs both.
          requiresPermission: [PERMISSIONS.READ_PO, PERMISSIONS.READ_CAPACITY],
        } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'po-tracker',
        name: 'PoTracker',
        component: PoTracker,
        meta: { title: 'PO Tracker | Manufacturing Partners Portal', requiresPermission: PERMISSIONS.READ_PO_TRACKER } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'po-activity',
        name: 'PoActivity',
        component: PoActivity,
        meta: { title: 'PO Activity | Manufacturing Partners Portal', requiresPermission: PERMISSIONS.READ_PO_ACTIVITY } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'capacity-assignment',
        name: 'CapacityAssignment',
        component: CapacityAssignment,
        meta: {
          title: 'PO Assignment | Manufacturing Partners Portal',
          requiresPermission: PERMISSIONS.READ_PO,
        } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'customer-mappings',
        name: 'CustomerMappingList',
        component: CustomerMappingList,
        meta: { title: 'Customer Mappings | Manufacturing Partners Portal', requiresPermission: PERMISSIONS.READ_CUSTOMER_MAPPINGS } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'manage-access',
        name: 'ManageAccess',
        component: ManageAccess,
        // The page's vendor picker lists the directory (GET /vendors), which needs read:vendors.
        meta: {
          title: 'Manage Access | Manufacturing Partners Portal',
          requiresPermission: [PERMISSIONS.MANAGE_ACCESS, PERMISSIONS.READ_VENDORS],
        } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
        meta: { title: 'Account | Manufacturing Partners Portal' } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: '404',
        name: 'NotFound',
        component: NotFound,
        meta: { title: '404 Not Found | Manufacturing Partners Portal' } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: '403',
        name: 'Forbidden',
        component: Forbidden,
        meta: { title: '403 Forbidden | Manufacturing Partners Portal' } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: Login,
        meta: { title: 'Login | Manufacturing Partners Portal' } as CustomRouteMeta,
        beforeEnter: createAuthGuard(),
      },
      {
        path: 'logout',
        name: 'Logout',
        component: Logout,
        meta: { title: 'Logout | Manufacturing Partners Portal' } as CustomRouteMeta,
      },
      // The profile page moved under MainLayout (sidebar chrome); keep the old
      // auth-shell URL working for bookmarks.
      {
        path: 'profile',
        redirect: { name: 'Profile' },
      },
    ],
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/404',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, _from, next) => {
  const meta = to.meta as CustomRouteMeta;
  document.title = meta.title || 'Manufacturing Partners Portal';
  next();
});

router.beforeEach(async (to, _from, next) => {
  const meta = to.meta as CustomRouteMeta;
  if (!meta.requiresPermission) {
    next();
    return;
  }

  // Auth0 bootstrap hasn't produced a token yet (e.g. a hard reload on this
  // very route): a /me call now would go out unauthenticated and fail,
  // making a real caller look permissionless. Let the navigation proceed —
  // MainLayout only renders a loader until /me loads — and rely on
  // MainLayout's post-load watcher (see accessGuard usage there) to enforce
  // access once we actually know who the caller is.
  if (!useAuthStore().credential) {
    next();
    return;
  }

  const accessStore = useAccessStore();
  await accessStore.load();

  // /me failed (e.g. transient server error): MainLayout's error/retry
  // screen handles presentation. Denying here would misreport a server
  // hiccup as "forbidden".
  if (!accessStore.loaded) {
    next();
    return;
  }

  const { hasPermission } = useAccess();
  const denied = resolveAccessDenial({ requiresPermission: meta.requiresPermission }, { hasPermission });

  if (denied) {
    next({ name: 'Forbidden' });
    return;
  }

  next();
});

export default router;
